/* Buxett — global.js
   Header, mobile menu, reveal, sticky ATC, newsletter popup,
   announcement fader, variant cards, pyramid sync */

(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── Header scroll state ────────────────────────────────
  const header = document.querySelector('[data-header]');
  const progressEl = document.querySelector('[data-scroll-progress]');
  const onScrollHeader = () => {
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 16);
    if (progressEl) {
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docH > 0 ? Math.min(100, (window.scrollY / docH) * 100) : 0;
      progressEl.style.width = pct + '%';
    }
  };
  onScrollHeader();
  window.addEventListener('scroll', onScrollHeader, { passive: true });

  // ── Parallax (hero product + any [data-parallax-target]) ──
  const parallaxTargets = document.querySelectorAll('[data-parallax-target]');
  if (!reduceMotion && parallaxTargets.length) {
    let ticking = false;
    const update = () => {
      parallaxTargets.forEach(el => {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight;
        if (rect.bottom < 0 || rect.top > vh) return;
        const center = rect.top + rect.height / 2;
        const offset = (center - vh / 2) * -0.12;
        el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      });
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  // ── Mobile menu ────────────────────────────────────────
  const mMenu = document.querySelector('[data-mobile-menu]');
  const closeMenu = () => {
    if (!mMenu) return;
    mMenu.classList.remove('is-open');
    mMenu.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };
  document.querySelectorAll('[data-menu-open]').forEach(b =>
    b.addEventListener('click', () => {
      if (!mMenu) return;
      mMenu.classList.add('is-open');
      mMenu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    })
  );
  document.querySelectorAll('[data-menu-close]').forEach(b =>
    b.addEventListener('click', closeMenu)
  );

  // ── Smooth-Scroll Anker-Links ──────────────────────────
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-anchor-link], a[href^="#"], a[href*="/#"]');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    let hash = '';
    if (href.startsWith('#')) {
      hash = href;
    } else if (href.includes('#')) {
      const url = new URL(href, window.location.origin);
      const samePath = url.pathname === window.location.pathname || url.pathname === '/' && (window.location.pathname === '/' || window.location.pathname === '');
      if (samePath) hash = '#' + url.hash.replace(/^#/, '');
    }
    if (!hash || hash === '#') return;
    const target = document.querySelector(hash);
    if (!target) return;
    e.preventDefault();
    closeMenu();
    const headerEl = document.querySelector('[data-header]');
    const offset = (headerEl ? headerEl.getBoundingClientRect().height : 0) + 12;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
    history.pushState(null, '', hash);
  });

  // ── Reveal on scroll ───────────────────────────────────
  const revealEls = document.querySelectorAll('.reveal');
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    revealEls.forEach(el => io.observe(el));
  } else {
    revealEls.forEach(el => el.classList.add('is-visible'));
  }

  // ── Announcement fader ─────────────────────────────────
  document.querySelectorAll('[data-announcement]').forEach(bar => {
    const items = Array.from(bar.querySelectorAll('[data-announcement-item]'));
    const dots  = Array.from(bar.querySelectorAll('[data-announcement-dot]'));
    if (items.length < 2) return;

    let idx = 0;
    let timer = null;
    const INTERVAL = 4500;

    const setActive = (next) => {
      idx = (next + items.length) % items.length;
      items.forEach((el, i) => el.classList.toggle('is-active', i === idx));
      dots.forEach((el, i) => el.classList.toggle('is-active', i === idx));
    };
    const start = () => {
      stop();
      if (reduceMotion) return;
      timer = window.setInterval(() => setActive(idx + 1), INTERVAL);
    };
    const stop = () => { if (timer) { window.clearInterval(timer); timer = null; } };

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { setActive(i); start(); });
    });
    bar.addEventListener('mouseenter', stop);
    bar.addEventListener('mouseleave', start);

    setActive(0);
    start();
  });

  // ── Pyramid: sync legend ↔ SVG layer ──────────────────
  document.querySelectorAll('[data-notes]').forEach(section => {
    const layers = section.querySelectorAll('.pyramid__layer');
    const rows   = section.querySelectorAll('[data-layer-row]');
    if (!layers.length || !rows.length) return;

    let pinned = null;

    const apply = (key) => {
      layers.forEach(l => l.classList.toggle('is-active', l.dataset.layer === key));
      rows.forEach(r => r.classList.toggle('is-active', r.dataset.layerRow === key));
    };
    const clear = () => {
      layers.forEach(l => l.classList.remove('is-active'));
      rows.forEach(r => r.classList.remove('is-active'));
    };

    rows.forEach(row => {
      const key = row.dataset.layerRow;
      row.setAttribute('tabindex', '0');
      row.addEventListener('mouseenter', () => { if (!pinned) apply(key); });
      row.addEventListener('mouseleave', () => { if (!pinned) clear(); });
      row.addEventListener('focusin',   () => { if (!pinned) apply(key); });
      row.addEventListener('focusout',  () => { if (!pinned) clear(); });
      row.addEventListener('click', () => {
        if (pinned === key) { pinned = null; clear(); }
        else { pinned = key; apply(key); }
      });
    });

    if (reduceMotion) return;
    const layerKeys = ['top', 'heart', 'base'];
    let cycle = null;
    let i = 0;
    const tick = () => {
      if (pinned) return;
      apply(layerKeys[i % layerKeys.length]); i++;
    };

    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          if (!cycle) { tick(); cycle = window.setInterval(tick, 2400); }
        } else if (cycle) {
          window.clearInterval(cycle); cycle = null;
          if (!pinned) clear();
        }
      });
    }, { threshold: 0.35 });
    io.observe(section);
  });

  // ── Smooth Bild-Wechsel (Crossfade + Scale) ────────────
  function smoothImageSwap(img, nextSrc, addClass, removeClass) {
    if (!img || !nextSrc) return;
    if (img.src === nextSrc || img.getAttribute('src') === nextSrc) return;

    const pre = new Image();
    pre.onload = () => {
      img.src = nextSrc;
      if (removeClass) img.classList.remove(removeClass);
      if (addClass)    img.classList.add(addClass);
      img.classList.remove('is-swapping');
      img.classList.add('is-entering');
      const onEnd = () => { img.classList.remove('is-entering'); img.removeEventListener('animationend', onEnd); };
      img.addEventListener('animationend', onEnd);
    };
    pre.onerror = () => { img.src = nextSrc; };
    pre.src = nextSrc;
  }
  window.__buxettSwapImage = smoothImageSwap;

  // ── Variant cards (radio-driven) + Tester-Bildwechsel ──
  document.querySelectorAll('.variant-cards').forEach(group => {
    const cards = group.querySelectorAll('.variant-card');
    const featureSection = group.closest('[data-feature-product]');
    const featureMedia   = featureSection?.querySelector('[data-feature-media]') || null;
    const defaultImg     = featureSection?.dataset.defaultImage || '';
    const testerImg      = featureSection?.dataset.testerImage  || '';

    const swapMedia = (label) => {
      if (!featureMedia) return;
      const isTester = /3\s*ml|tester|probe/i.test(label || '');
      const next = isTester ? testerImg : defaultImg;
      if (!next) return;
      smoothImageSwap(featureMedia, next, isTester ? 'is-tester' : null, isTester ? null : 'is-tester');
    };

    cards.forEach(card => {
      const radio = card.querySelector('input[type="radio"]');
      const apply = () => {
        cards.forEach(c => c.classList.remove('is-active'));
        card.classList.add('is-active');
        const id = card.dataset.variantId;
        if (id) {
          const form = card.closest('form');
          const idInput = form?.querySelector('[data-variant-input]') ||
                          form?.querySelector('input[name="id"]');
          if (idInput) idInput.value = id;
          const priceEl = document.querySelector('[data-product-section] [data-price]');
          if (priceEl && card.dataset.price) priceEl.textContent = card.dataset.price;
        }
        const titleEl = card.querySelector('.variant-card__title');
        swapMedia(titleEl?.textContent || '');

        const stickyAtc = document.querySelector('[data-sticky-atc]');
        if (stickyAtc) {
          const stickyPrice = stickyAtc.querySelector('[data-price]');
          if (stickyPrice && card.dataset.price) stickyPrice.textContent = card.dataset.price;
        }
      };
      card.addEventListener('click', () => {
        if (card.classList.contains('is-soldout')) return;
        if (radio) radio.checked = true;
        apply();
      });
      radio?.addEventListener('change', () => { if (radio.checked) apply(); });
    });

    const active = group.querySelector('.variant-card.is-active');
    if (active) swapMedia(active.querySelector('.variant-card__title')?.textContent || '');
  });

  // ── PDP Galerie (Slides + Thumbs + Filter nach Größe) ─
  document.querySelectorAll('[data-ps-gallery]').forEach(g => {
    const slides = g.querySelectorAll('.ps-gallery__slide');
    const thumbs = g.querySelectorAll('.ps-gallery__thumb');
    const prev = g.querySelector('[data-ps-prev]');
    const next = g.querySelector('[data-ps-next]');
    let currentSize = '100';

    const visibleSlides = () => Array.from(slides).filter(s => s.dataset.size === currentSize);
    const visibleThumbs = () => Array.from(thumbs).filter(t => t.dataset.size === currentSize);

    const showSlide = (idx) => {
      const v = visibleSlides();
      const t = visibleThumbs();
      if (!v.length) return;
      const i = ((idx % v.length) + v.length) % v.length;
      slides.forEach(s => s.classList.remove('is-active'));
      thumbs.forEach(th => th.classList.remove('is-active'));
      v[i].classList.add('is-active');
      if (t[i]) t[i].classList.add('is-active');
    };

    const filterBy = (size) => {
      currentSize = String(size);
      slides.forEach(s => s.style.display = (s.dataset.size === currentSize) ? '' : 'none');
      thumbs.forEach(t => t.style.display = (t.dataset.size === currentSize) ? '' : 'none');
      const v = visibleSlides();
      const t = visibleThumbs();
      slides.forEach(s => s.classList.remove('is-active'));
      thumbs.forEach(th => th.classList.remove('is-active'));
      if (v[0]) v[0].classList.add('is-active');
      if (t[0]) t[0].classList.add('is-active');
      if (prev) prev.style.display = v.length > 1 ? '' : 'none';
      if (next) next.style.display = v.length > 1 ? '' : 'none';
    };

    thumbs.forEach((t) => t.addEventListener('click', () => {
      const v = visibleThumbs();
      const idx = v.indexOf(t);
      if (idx >= 0) showSlide(idx);
    }));
    prev?.addEventListener('click', () => {
      const v = visibleSlides();
      const cur = v.findIndex(s => s.classList.contains('is-active'));
      showSlide(cur - 1);
    });
    next?.addEventListener('click', () => {
      const v = visibleSlides();
      const cur = v.findIndex(s => s.classList.contains('is-active'));
      showSlide(cur + 1);
    });

    filterBy(currentSize);

    const section = g.closest('[data-product-section]');
    if (section) {
      section.addEventListener('click', (e) => {
        const btn = e.target.closest('.ps-size, .variant');
        if (!btn) return;
        const value = (btn.dataset.optionValue || '').toLowerCase();
        const isTester = btn.dataset.isTester === 'true' || /3\s*ml|tester|probe/.test(value);
        filterBy(isTester ? '3' : '100');
      });
    }
  });

  // ── PDP Größe-Summary + Klarna-Rate Updates ───────────
  document.querySelectorAll('[data-product-section]').forEach(section => {
    let productData = null;
    try { productData = JSON.parse(section.dataset.productJson || 'null'); } catch (_) {}
    const summary = section.querySelector('[data-size-summary]');
    const klarna = section.querySelector('[data-klarna]');
    const klarnaRate = section.querySelector('[data-klarna-rate]');

    const fmt = (cents) => window.formatMoney ? window.formatMoney(cents) : ((cents / 100).toFixed(2).replace('.', ',') + ' €');

    // ── FIX: Streichpreis (compare_at_price) wird jetzt auch im Summary mitgerendert ──
    const updateInfo = (variant) => {
      if (!variant) return;
      if (summary) {
        const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
        const wasHtml = hasDiscount
          ? `<span class="product__price-was">${fmt(variant.compare_at_price)}</span> `
          : '';
        summary.innerHTML = `${variant.title} · ${wasHtml}<span data-price-display>${fmt(variant.price)}</span>`;
      }
      if (klarna) {
        const rate = Math.round(variant.price / 3);
        const isFlakon = !/3\s*ml|tester|probe/i.test(variant.title);
        klarna.style.display = isFlakon ? '' : 'none';
        if (klarnaRate) klarnaRate.textContent = `3× ${fmt(rate)}`;
      }
    };

    section.addEventListener('click', (e) => {
      const btn = e.target.closest('.variant, .ps-size');
      if (!btn || !productData) return;
      const groups = section.querySelectorAll('[data-option-name]');
      const selected = Array.from(groups).map(g => {
        const a = g.querySelector('.is-active, .variant.is-active, .ps-size.is-active');
        return a ? a.dataset.optionValue : null;
      });
      const variant = productData.variants.find(v => v.options.every((opt, i) => opt === selected[i]));
      if (variant) updateInfo(variant);
    });

    if (productData) {
      const initial = productData.variants.find(v => v.id === (productData.selected_or_first_available_variant?.id));
      if (initial) updateInfo(initial);
    }
  });

  // ── PDP variant picker (option pills → variant id) ────
  document.querySelectorAll('[data-product-section]').forEach(section => {
    let productData = null;
    try { productData = JSON.parse(section.dataset.productJson || 'null'); } catch (_) {}
    if (!productData || !Array.isArray(productData.variants)) return;

    const optionGroups = Array.from(section.querySelectorAll('[data-option-name]'));
    const idInput = section.querySelector('[data-variant-input]') || section.querySelector('input[name="id"]');
    const priceEl = section.querySelector('[data-price]');
    const priceWasEl = section.querySelector('.product__price-was');
    const submitBtn = section.querySelector('[data-add-to-cart]');
    if (!optionGroups.length || !idInput) return;

    const formatMoney = (cents) => window.formatMoney ? window.formatMoney(cents) : ((cents / 100).toFixed(2).replace('.', ',') + ' €');

    const getSelected = () => optionGroups.map(g => {
      const active = g.querySelector('.variant.is-active');
      return active ? active.dataset.optionValue : null;
    });

    const findVariant = (selected) => productData.variants.find(v => {
      return v.options.every((opt, i) => opt === selected[i]);
    });

    const update = () => {
      const selected = getSelected();
      const variant = findVariant(selected);
      if (!variant) {
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Nicht verfügbar'; }
        return;
      }
      idInput.value = variant.id;
      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
        priceEl.classList.remove('is-pulsing');
        void priceEl.offsetWidth;
        priceEl.classList.add('is-pulsing');
      }
      if (priceWasEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceWasEl.textContent = formatMoney(variant.compare_at_price);
          priceWasEl.style.display = '';
        } else {
          priceWasEl.style.display = 'none';
        }
      }
      if (submitBtn) {
        submitBtn.disabled = !variant.available;
        if (!variant.available && submitBtn.textContent !== 'Ausverkauft') submitBtn.textContent = 'Ausverkauft';
      }
      // Sticky ATC Preis sync
      const stickyPrice = document.querySelector('[data-sticky-atc] [data-price]');
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
    };

    const mediaImg = section.querySelector('[data-product-media]');
    const defaultImg = section.dataset.defaultImage || '';
    const testerImg  = section.dataset.testerImage  || '';
    const setMedia = (selectedValues) => {
      if (!mediaImg) return;
      const isTester = selectedValues.some(v => v && /3\s*ml|tester|probe/i.test(v));
      const next = isTester ? testerImg : defaultImg;
      if (!next) return;
      smoothImageSwap(mediaImg, next, isTester ? 'is-tester' : null, isTester ? null : 'is-tester');
    };

    optionGroups.forEach(group => {
      group.querySelectorAll('.variant').forEach(btn => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('.variant').forEach(b => {
            b.classList.remove('is-active');
            b.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('is-active');
          btn.setAttribute('aria-pressed', 'true');
          update();
          setMedia(getSelected());
        });
      });
    });

    setMedia(getSelected());
  });

  // ── Sticky ATC ─────────────────────────────────────────
  (() => {
    const sticky = document.querySelector('[data-sticky-atc]');
    if (!sticky) return;

    const sentinel =
      document.querySelector('[data-product-section] [data-add-to-cart]') ||
      document.querySelector('[data-feature-product] [data-add-to-cart]') ||
      document.querySelector('.product__actions [data-add-to-cart]');

    if (sentinel) {
      const stickyIO = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          const visible = !e.isIntersecting && e.boundingClientRect.top < 0;
          sticky.classList.toggle('is-visible', visible);
          sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
        });
      }, { threshold: 0 });
      stickyIO.observe(sentinel);
    }

    const stickyAdd = sticky.querySelector('[data-sticky-add]');
    if (stickyAdd) {
      stickyAdd.addEventListener('click', async () => {
        const idInput =
          document.querySelector('[data-product-section] [data-variant-input]') ||
          document.querySelector('[data-product-section] input[name="id"]') ||
          document.querySelector('[data-feature-product] input[name="id"]');

        const variantId = idInput ? idInput.value : null;
        if (!variantId) {
          sentinel?.click();
          return;
        }

        const originalText = stickyAdd.textContent;
        stickyAdd.disabled = true;
        stickyAdd.textContent = '…';

        try {
          const qtyInput =
            document.querySelector('[data-product-section] .qty input') ||
            document.querySelector('[data-feature-product] .qty input');
          const qty = parseInt(qtyInput?.value || '1', 10) || 1;

          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ id: variantId, quantity: qty })
          });

          if (!res.ok) throw new Error('Fehler beim Hinzufügen');

          const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
          const cart = await cartRes.json();

          document.querySelectorAll('[data-cart-count]').forEach(el => {
            el.textContent = cart.item_count;
            el.dataset.count = cart.item_count;
          });

          const drawer = document.querySelector('[data-cart-drawer]');
          const overlay = document.querySelector('[data-cart-overlay]');
          if (drawer && overlay) {
            if (typeof window.__buxettRenderDrawer === 'function') {
              window.__buxettRenderDrawer(cart);
            }
            drawer.classList.add('is-open');
            overlay.classList.add('is-open');
            drawer.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
          }

          stickyAdd.textContent = '✓ Hinzugefügt';
          setTimeout(() => { stickyAdd.textContent = originalText; }, 1800);
        } catch (err) {
          console.warn('Sticky ATC Fehler:', err);
          stickyAdd.textContent = 'Fehler – erneut versuchen';
          setTimeout(() => { stickyAdd.textContent = originalText; }, 1800);
        } finally {
          stickyAdd.disabled = false;
        }
      });
    }
  })();

  // ── Quantity stepper (any qty) ─────────────────────────
  document.addEventListener('click', (e) => {
    const down = e.target.closest('[data-qty-down]');
    const up = e.target.closest('[data-qty-up]');
    if (!down && !up) return;
    const wrap = (down || up).closest('.qty');
    const input = wrap?.querySelector('input');
    if (!input) return;
    const min = parseInt(input.min || '1', 10);
    const n = parseInt(input.value, 10) || min;
    input.value = down ? Math.max(min, n - 1) : n + 1;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // ── Newsletter popup ───────────────────────────────────
  const np = document.querySelector('[data-np]');
  if (np) {
    const isCartPage = window.location.pathname.includes('/cart');
    const KEY = 'buxett_np_dismissed';
    const dismissed = localStorage.getItem(KEY);

    if (!dismissed && !isCartPage) {
      const open = () => {
        np.classList.add('is-open');
        np.setAttribute('aria-hidden', 'false');
      };
      const close = () => {
        np.classList.remove('is-open');
        np.setAttribute('aria-hidden', 'true');
        localStorage.setItem(KEY, Date.now().toString());
      };
      let shown = false;
      const showOnce = () => { if (!shown) { shown = true; open(); } };
      window.setTimeout(showOnce, 18000);
      document.addEventListener('mouseout', (e) => {
        if (e.clientY <= 0 && !shown) showOnce();
      });
      np.querySelector('[data-np-close]')?.addEventListener('click', close);
      np.addEventListener('click', (e) => { if (e.target === np) close(); });
    }
  }

  // ── Helper exposed for cart.js ─────────────────────────
  function formatMoney(cents) {
    const eur = (cents / 100).toFixed(2).replace('.', ',');
    return eur + ' €';
  }
  window.formatMoney = formatMoney;
})();